import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { HiChevronDown } from "react-icons/hi";
import NavUnreadPill from "./NavUnreadPill";

// Desktop top-bar navigation.
//
// Items flagged `overflow: true` (e.g. "Agent Tenders") are secondary: they
// stay inline only on wide screens (xl and up) and collapse into a compact
// "More" dropdown across the whole narrow-to-medium desktop band (md → xl).
// This keeps the core items AND the search box from being squeezed off the
// row at ~half-width, while every destination stays reachable at every width.
// Roles whose nav has no overflow items render exactly as before (no "More"
// button appears). xl (not lg) is the reveal point because at ~1024–1279 the
// row is still too tight to re-inline the extra item without overflowing.
const linkClass = (active) =>
  `px-3 py-2 rounded-[10px] text-sm font-medium whitespace-nowrap transition-colors ${
    active
      ? "text-[#2283FF] bg-[#EEF4FE]"
      : "text-[#4B5563] hover:bg-[#F3F6FB] hover:text-[#0F1419]"
  }`;

const NavigationMenu = ({ navigationItems, isActive }) => {
  const primaryItems = navigationItems.filter((item) => !item.overflow);
  const overflowItems = navigationItems.filter((item) => item.overflow);

  const [moreOpen, setMoreOpen] = useState(false);
  const moreRef = useRef(null);

  // Close the "More" menu on any outside click.
  useEffect(() => {
    if (!moreOpen) return undefined;
    const onDown = (e) => {
      if (moreRef.current && !moreRef.current.contains(e.target)) {
        setMoreOpen(false);
      }
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [moreOpen]);

  return (
    <nav className="hidden xl:flex items-center gap-0.5 flex-shrink-0">
      {primaryItems.map((item) => (
        <Link
          key={item.path}
          to={item.path}
          className={`inline-flex items-center gap-1.5 ${linkClass(isActive(item.path))}`}
        >
          {item.label}
          {/* D26: global chat-unread pill (only the /inbox item carries a count). */}
          <NavUnreadPill count={item.unread} />
        </Link>
      ))}

      {/* Overflow items — inline only from xl up. */}
      {overflowItems.map((item) => (
        <Link
          key={item.path}
          to={item.path}
          className={`hidden xl:inline-flex ${linkClass(isActive(item.path))}`}
        >
          {item.label}
        </Link>
      ))}

      {/* "More" dropdown — on the narrow-to-medium desktop band (below xl). */}
      {overflowItems.length > 0 && (
        <div className="relative xl:hidden" ref={moreRef}>
          <button
            type="button"
            onClick={() => setMoreOpen((v) => !v)}
            className={`flex items-center gap-0.5 ${linkClass(
              overflowItems.some((i) => isActive(i.path)),
            )}`}
            aria-haspopup="true"
            aria-expanded={moreOpen}
          >
            More
            <HiChevronDown className="w-4 h-4" />
          </button>
          {moreOpen && (
            <div className="absolute left-0 mt-2 w-48 bg-white border rounded-lg shadow-lg py-1 z-50">
              {overflowItems.map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={() => setMoreOpen(false)}
                  className={`block px-4 py-2 text-sm hover:bg-gray-50 ${
                    isActive(item.path) ? "text-blue-600" : "text-black"
                  }`}
                >
                  {item.label}
                </Link>
              ))}
            </div>
          )}
        </div>
      )}
    </nav>
  );
};

export default NavigationMenu;
